import React, { useCallback, useState } from 'react'
import { SessionContext } from '../generated/graphql'
import { ReconciliationStep } from '../models/reconciliation'
import { ReconciliationRecordType } from '../pages/Reconciliation/Layout/RecordSelector'
import { mapToStep } from '../utils/stepMap'
import useLocalStorage from './useLocalStorage'

interface ReconciliationProviderProps {
  children: React.ReactNode
}

export interface Context {
  taxYear: number
  accountId: number
  accountNumber: string
  businessName: string
  completedSteps: ReconciliationStep[]
  fullName: string
  buildingPermitExists: boolean
}

interface ReconciliationContextType {
  reconciliationContext: Context | null
  mainWorkflowCompleted: boolean
  recordSlotA: string
  recordSlotB: string
  disableRecordSelector?: boolean
  setRecordSlotA?: (record: string) => void
  setRecordSlotB?: (record: string) => void
  setDisableRecordSelector?: (disable: boolean) => void
  setMainWorkflowCompleted?: (completed: boolean) => void
  storeContext?: (sessionContext: SessionContext) => void
  removeContext?: () => void
}

export const ReconciliationContext = React.createContext<ReconciliationContextType>({} as ReconciliationContextType)

export function ReconciliationProvider({ children }: ReconciliationProviderProps): JSX.Element {
  const [reconciliationContext, setReconciliationContext] = useLocalStorage<Context | null>(
    'reconciliationContext',
    null,
  )

  const [disableRecordSelector, setDisableRecordSelector] = useState(false)
  const [recordSlotA, setRecordSlotA] = useState<string>(ReconciliationRecordType.TaxPayerVersion)
  const [recordSlotB, setRecordSlotB] = useState<string>(ReconciliationRecordType.AuditorVersion)
  const [mainWorkflowCompleted, setMainWorkflowCompleted] = useState(false)

  const storeContext = useCallback(
    async (sessionContext: SessionContext): Promise<void> => {
      const completedSteps = sessionContext.completedSteps.map(mapToStep)
      const context = {
        taxYear: sessionContext.taxYear,
        accountId: sessionContext.accountId,
        accountNumber: sessionContext.accountNumber,
        businessName: sessionContext.businessName,
        fullName: sessionContext.fullName,
        buildingPermitExists: sessionContext.buildingPermitExists ?? false,
        completedSteps,
      }
      setReconciliationContext(context)
    },
    [setReconciliationContext],
  )

  const removeContext = useCallback((): void => {
    setReconciliationContext(null)
  }, [setReconciliationContext])

  const context = {
    reconciliationContext,
    mainWorkflowCompleted,
    recordSlotA,
    recordSlotB,
    disableRecordSelector,
    setRecordSlotA,
    setRecordSlotB,
    setDisableRecordSelector,
    setMainWorkflowCompleted,
    storeContext,
    removeContext,
  }

  return <ReconciliationContext.Provider value={context}>{children}</ReconciliationContext.Provider>
}

export default function useReconciliationContext(): ReconciliationContextType {
  return React.useContext(ReconciliationContext)
}
